import React, { useState, useRef, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { UserRound, Upload } from 'lucide-react';

const PLANOS = [
  {
    id: 'basic',
    label: 'Básico',
    price: 'R$ 19,90',
    features: ['Qualidade de vídeo HD', '1 tela simultânea', 'Assista no celular, tablet ou TV']
  },
  {
    id: 'standard',
    label: 'Padrão',
    price: 'R$ 29,90',
    features: ['Qualidade de vídeo FULL HD', '2 telas simultâneas', 'Assista no celular, tablet ou TV']
  },
  {
    id: 'premium',
    label: 'Premium',
    price: 'R$ 39,90',
    features: ['Qualidade de vídeo 4K', '4 telas simultâneas', 'Assista no celular, tablet ou TV', 'Conteúdo exclusivo']
  }
];

const Signup = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedPlanLabel, setSelectedPlanLabel] = useState('');
  const [selectedPlanPrice, setSelectedPlanPrice] = useState('');
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'credit' | 'pix'>('credit');
  const [showModal, setShowModal] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const video = document.getElementById('bgVideo') as HTMLVideoElement | null;
    if (window.innerWidth < 768 && video) {
      video.pause();
      video.style.display = 'none';
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("As senhas não correspondem!");
      return;
    }

    if (!selectedPlan) {
      toast.error("Por favor, selecione um plano antes de continuar.");
      return;
    }

    if (!email.includes('@')) {
      toast.error("Insira um e-mail válido!");
      return;
    }

    localStorage.setItem('user', JSON.stringify({
      fullName,
      email,
      profilePhoto: profilePhoto || '/placeholder.svg',
      selectedPlan,
      isRegistered: true,
      isSubscribed: false
    }));

    setShowModal(true);
  };

  const handlePlanSelection = (planId: string) => {
    const plan = PLANOS.find(p => p.id === planId);
    if (plan) {
      setSelectedPlan(plan.id);
      setSelectedPlanLabel(plan.label);
      setSelectedPlanPrice(plan.price);
      setShowPayment(true);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfilePhoto(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleContinue = () => {
    setShowModal(false);
    navigate('/home');
  };

  return (
    <>
      

<style>{`
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
  }
  ::-webkit-scrollbar-thumb {
    background-color: #52432f;
    border-radius: 6px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: #6f5f3f;
  }
  * {
    scrollbar-width: thin;
    scrollbar-color: #52432f rgba(0,0,0,0.3);
  }
`}</style>

<div className="signup-container min-h-screen w-full flex justify-center items-center relative overflow-hidden pt-[80px] sm:pt-[60px]">
        <div className="video-container absolute top-0 left-0 w-full h-full z-[-1] overflow-hidden">
          <video autoPlay muted loop playsInline id="bgVideo" className="min-w-full min-h-full object-cover opacity-50">
            <source src="/video/intro.mp4" type="video/mp4" />
          </video>
        </div>

        <nav className="navbar absolute top-2 left-4 z-10">
          <img
            src="/img/logo.png"
            alt="RusticoPlay Logo"
            className="h-[50px] sm:h-[70px] w-auto max-w-[160px] sm:max-w-[200px]"
          />
        </nav>

        <div className="signup-box bg-[rgba(0,0,0,0.3)] p-10 rounded-lg w-full max-w-[800px] text-center z-[1] border border-rustico-brown overflow-y-auto max-h-[95vh]">
          <h2 className="mb-5 text-[2rem] font-bold text-white">Crie sua conta</h2>

          <form onSubmit={handleSubmit}>
            <div className="photo-upload flex justify-center mb-6">
              <div className="relative w-24 h-24 rounded-full overflow-hidden bg-gray-700 border-2 border-rustico-brown cursor-pointer flex items-center justify-center" onClick={triggerFileInput}>
                {profilePhoto ? (
                  <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center justify-center text-white">
                    <UserRound size={32} />
                    <span className="text-xs mt-1">Foto</span>
                  </div>
                )}
                <div className="absolute bottom-0 right-0 bg-rustico-brown rounded-full p-1">
                  <Upload size={14} className="text-white" />
                </div>
              </div>
              <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" className="hidden" />
            </div>

            {[{ type: 'text', placeholder: 'Nome completo', value: fullName, setter: setFullName },
              { type: 'email', placeholder: 'Email', value: email, setter: setEmail },
              { type: 'password', placeholder: 'Senha', value: password, setter: setPassword },
              { type: 'password', placeholder: 'Confirme sua senha', value: confirmPassword, setter: setConfirmPassword }
            ].map((field, idx) => (
              <div className="input-group relative mb-5" key={idx}>
                <input
                  type={field.type}
                  placeholder={field.placeholder}
                  required
                  value={field.value}
                  onChange={(e) => field.setter(e.target.value)}
                  className="w-full p-[12px] bg-[#1c1c1c] text-white text-base outline-none border-b-2 border-rustico-brown rounded-md placeholder:text-white/60"
                />
              </div>
            ))}

            <div className="plans mt-8">
              <h3 className="text-[1.5rem] mb-5 text-white">Escolha seu plano:</h3>
              <div className="flex gap-5 justify-center flex-wrap">
                {PLANOS.map(plan => (
                  <div key={plan.id} className={`bg-white/10 p-5 rounded-lg w-full max-w-[200px] text-center transition-all duration-300 hover:scale-105 hover:bg-white/20 ${selectedPlan === plan.id ? 'ring-2 ring-rustico-brown' : ''}`}>
                    <h4 className="text-[1.25rem] mb-[10px] text-white">{plan.label}</h4>
                    <p className="text-[1.5rem] font-bold mb-[15px] text-white">
                      {plan.price}<span className="text-base text-[#8c8c8c]">/mês</span>
                    </p>
                    <ul className="list-none p-0 mb-5">
                      {plan.features.map((desc, i) => (
                        <li key={i} className="text-[0.9rem] mb-[10px] text-[#8c8c8c]">{desc}</li>
                      ))}
                    </ul>
                    <button type="button" onClick={() => handlePlanSelection(plan.id)} className="w-full p-[10px] bg-rustico-brown rounded-md text-white text-sm transition-colors hover:bg-[#6f5f3f]">
                      {selectedPlan === plan.id ? 'Selecionado' : 'Selecionar'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {showPayment && (
              <div className="mt-6">
                <h4 className="text-lg font-semibold mb-3 text-white">Forma de pagamento</h4>
                <div className="flex gap-4 justify-center mb-4">
                  <button type="button" onClick={() => setPaymentMethod('credit')} className={`px-4 py-2 rounded-md ${paymentMethod === 'credit' ? 'bg-rustico-brown text-white' : 'bg-white text-black'}`}>Cartão de Crédito</button>
                  <button type="button" onClick={() => setPaymentMethod('pix')} className={`px-4 py-2 rounded-md ${paymentMethod === 'pix' ? 'bg-rustico-brown text-white' : 'bg-white text-black'}`}>PIX</button>
                </div>

                {paymentMethod === 'credit' && (
                  <div className="text-white">
                    <div className="flex items-center gap-2 mb-4">
                      <img src="/src/assets/img/Visa.png" alt="Visa" className="h-6" />
                      <img src="/src/assets/img/MasterCard.png" alt="MasterCard" className="h-6" />
                      <img src="/src/assets/img/amex.png" alt="Amex" className="h-6" />
                    </div>
                    <input type="text" placeholder="Nome no cartão" className="mb-2 w-full p-2 rounded bg-[#1c1c1c]" required />
                    <input type="text" placeholder="Número do cartão" maxLength={19} className="mb-2 w-full p-2 rounded bg-[#1c1c1c]" required />
                    <div className="flex gap-4">
                      <input type="text" placeholder="MM/AA" maxLength={5} className="mb-2 w-1/2 p-2 rounded bg-[#1c1c1c]" required />
                      <input type="text" placeholder="CVV" maxLength={3} className="mb-2 w-1/2 p-2 rounded bg-[#1c1c1c]" required />
                    </div>
                  </div>
                )}

                {paymentMethod === 'pix' && (
                  <div className="text-white text-center">
                    <div className="mb-4">
                      <div className="w-32 h-32 flex items-center justify-center rounded mx-auto overflow-hidden">
                        <QRCodeCanvas value="00020126580014BR.GOV.BCB.PIX0136example-pix-key@example.com" size={128} bgColor="#FFFFFF" fgColor="#000000" level="H" />
                      </div>
                      <p className="mt-2">Escaneie o QR Code para pagar</p>
                    </div>
                    <p>Ou copie o código abaixo:</p>
                    <div className="flex items-center gap-2 justify-center mt-2">
                      <input type="text" value="00020126580014BR.GOV.BCB.PIX0136example-pix-key@example.com" readOnly className="p-2 w-[320px] rounded bg-[#1c1c1c] text-white" />
                      <button type="button" onClick={() => navigator.clipboard.writeText('00020126580014BR.GOV.BCB.PIX0136example-pix-key@example.com')} className="bg-rustico-brown text-white px-3 py-2 rounded hover:bg-[#6f5f3f]">Copiar</button>
                    </div>
                  </div>
                )}

                <div className="order-summary text-left text-white mt-6">
                  <h4 className="text-lg font-semibold mb-3">Resumo da assinatura</h4>
                  <div className="bg-white/10 rounded-md p-4">
                    <div className="flex justify-between mb-2">
                      <span>Plano</span>
                      <span>{selectedPlanLabel}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span>Valor mensal</span>
                      <span>{selectedPlanPrice}</span>
                    </div>
                    <div className="border-t border-white/30 my-2"></div>
                    <div className="flex justify-between font-bold">
                      <span>Total</span>
                      <span>{selectedPlanPrice}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button type="submit" className="w-full mt-8 p-[12px] bg-white/20 text-white font-bold rounded-md hover:bg-white/30 transition-colors">
              Continuar para pagamento
            </button>
          </form>

          <p className="mt-5 text-[0.9rem] text-[#8c8c8c]">
            Já tem uma conta?{' '}
            <Link to="/login" className="text-[#ff003c] font-bold hover:underline">
              Faça login
            </Link>.
          </p>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg text-center max-w-md w-full">
            <h3 className="text-xl font-bold text-green-700 mb-4">Cadastro realizado com sucesso!</h3>
            <p className="mb-6 text-black">Seja bem-vindo ao RusticoPlay, o melhor do sertanejo em um só lugar.</p>
            <button onClick={handleContinue} className="bg-rustico-brown text-white py-2 px-6 rounded-md hover:bg-[#6f5f3f] transition">
              Continuar
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Signup;
